import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, MessageSquare, Loader2, Copy, Check, LogIn, ArrowLeft, Lock } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';
import InsightToggleView from './insight/InsightToggleView';
import { hasRenderableVisual } from './insight/util';
import { sharedAudienceLine } from './sharedAudienceLine';
import {
  continueHeading,
  continueLabel,
  continuePath,
  continuePrompt,
  homePath,
  isPlatformReader,
  showsEnterpriseNav,
  signInLabel,
  signInPath,
} from './sharedChatOrigin';
import type { VisualPayload } from './insight/types';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  // Snapshotted message metadata; carries the visual (Insights) payload when the
  // answer had one, so the shared view can show the same Insights | Text Analysis tabs.
  metadata?: { visual?: VisualPayload | null } | null;
}

interface SharedChatData {
  session_id: number;
  title: string | null;
  messages: ChatMessage[];
  segment_id: number;
  created_at: string;
  shared_at: string;
  can_fork: boolean;
  view_count: number;
  is_owner: boolean;
  has_segment_access: boolean;
  /** Which audience answered — the cohort for a marketplace chat, else the segment
   *  name. Null when neither could be resolved (then no audience line is shown). */
  audience_label?: string | null;
  /** Size of the scoped audience; null on a platform chat, which has none. */
  twin_count?: number | null;
  /** Which product the chat came from — 'marketplace' | 'platform'. Drives which
   *  login the sign-in CTA offers, since the two have separate accounts. Absent on
   *  a payload from an older backend, which is then treated as platform. */
  origin?: string | null;
  /** The marketplace study this chat belongs to. Where a marketplace reader
   *  continues, since the platform fork destination is a route they can't open. */
  marketplace_study_id?: number | null;
}

// Same markdown components as AskNaturalOrganicChat
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

const SharedChatView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  // The share's origin says where the chat came from; this says who is reading it. A
  // platform account keeps the platform behaviour even on a marketplace share.
  const platformReader = isPlatformReader(isAuthenticated, user?.account_type);

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const hasAutoForked = useRef(false);

  const [sharedChat, setSharedChat] = useState<SharedChatData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isForking, setIsForking] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchSharedChat = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        // Only send credentials if authenticated (to check ownership)
        const fetchOptions: RequestInit = isAuthenticated ? { credentials: 'include' } : {};

        const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}`, fetchOptions);
        if (!res.ok) {
          if (res.status === 404) {
            setError('This shared chat was not found or has expired');
          } else {
            setError('Failed to load shared chat');
          }
          setIsLoading(false);
          return;
        }

        const data = await res.json();
        setSharedChat(data);
      } catch (err) {
        console.error('Error fetching shared chat:', err);
        setError('Failed to load shared chat');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedChat();
  }, [shareToken, isAuthenticated]);

  // Auto-fork if returning from login with fork=true param
  useEffect(() => {
    const shouldAutoFork = searchParams.get('fork') === 'true';

    if (
      shouldAutoFork &&
      isAuthenticated &&
      sharedChat &&
      !sharedChat.is_owner &&
      sharedChat.can_fork &&
      sharedChat.has_segment_access &&
      !hasAutoForked.current &&
      !isForking
    ) {
      hasAutoForked.current = true;
      // A marketplace reader has no platform session to fork into (the new session
      // route bounces them) — send them straight to their study instead.
      const marketplacePath = continuePath(
        sharedChat.origin,
        sharedChat.marketplace_study_id,
        platformReader,
        sharedChat.is_owner,
      );
      if (marketplacePath) {
        navigate(marketplacePath, { replace: true });
        return;
      }
      // Trigger fork
      (async () => {
        setIsForking(true);
        try {
          const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}/fork`, {
            method: 'POST',
            credentials: 'include',
          });
          if (res.ok) {
            const data = await res.json();
            navigate(`/ask-natural-organic/${data.new_session_id}`, { replace: true });
          } else if (res.status === 403) {
            // User doesn't have access - update local state
            setSharedChat((prev) =>
              prev ? { ...prev, has_segment_access: false, can_fork: false } : null,
            );
          }
        } catch (err) {
          console.error('Auto-fork failed:', err);
        } finally {
          setIsForking(false);
        }
      })();
    }
  }, [searchParams, isAuthenticated, platformReader, sharedChat, shareToken, navigate, isForking]);

  const handleFork = async () => {
    if (!isAuthenticated) {
      // Send them to the login that matches the share's product. A marketplace
      // reader signing in at /auth would still be logged out in the marketplace
      // (separate account_type) and couldn't open the forked platform chat.
      // No router state: the marketplace CTA now offers a NEW study, so the gate's
      // default signup form is the right landing (it used to force the login form to
      // match a "Sign in to the Marketplace" button that no longer exists).
      navigate(signInPath(sharedChat?.origin, shareToken));
      return;
    }

    // A marketplace chat continues inside its study. Forking would create a platform
    // session the reader is bounced off — an orphan session plus a trip to the home
    // page, which is what was reported.
    const marketplacePath = continuePath(
      sharedChat?.origin,
      sharedChat?.marketplace_study_id,
      platformReader,
      sharedChat?.is_owner,
    );
    if (marketplacePath) {
      navigate(marketplacePath);
      return;
    }

    setIsForking(true);
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/shared/${shareToken}/fork`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 403) {
          // User doesn't have access - update local state to reflect this
          setSharedChat((prev) =>
            prev ? { ...prev, has_segment_access: false, can_fork: false } : null,
          );
          alert("You don't have access to fork this chat. Contact your administrator for access.");
        } else {
          console.error('Failed to fork chat:', errorData.detail || 'Unknown error');
        }
        return;
      }

      const data = await res.json();
      navigate(`/ask-natural-organic/${data.new_session_id}`);
    } catch (err) {
      console.error('Error forking chat:', err);
    } finally {
      setIsForking(false);
    }
  };

  const handleCopyMessage = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // The share's origin isn't known until the payload lands, so these two transient
  // states can only go on the READER: a platform account gets the nav it can use (and
  // no content shift when the payload arrives); anyone else gets none, because showing
  // the enterprise nav to a marketplace reader is the defect being fixed. The error
  // card carries its own "Go Home".
  if (isLoading) {
    return (
      <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
        {platformReader && <Sidebar />}
        <div className={`flex-1 flex flex-col overflow-hidden ${platformReader ? 'md:ml-16' : ''}`}>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
              <p className="text-sm text-[#6B7280]">Loading shared chat...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sharedChat) {
    return (
      <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
        {platformReader && <Sidebar />}
        <div className={`flex-1 flex flex-col overflow-hidden ${platformReader ? 'md:ml-16' : ''}`}>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md px-4">
              <div className="w-16 h-16 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-[#EF4444]" />
              </div>
              <h1 className="text-xl font-semibold text-[#1C1C1E] mb-2">Chat Not Found</h1>
              <p className="text-[#6B7280] mb-6">
                {error || 'This shared chat could not be loaded.'}
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // "5 Twins · Cereal › Protein Purists" — null when the backend couldn't name the
  // audience (an older payload, or an unresolvable segment), and then no line renders.
  const audienceLine = sharedAudienceLine(sharedChat.audience_label, sharedChat.twin_count);

  // Every link in the enterprise nav is a platform route a marketplace account is
  // bounced off, so a marketplace share renders without it (and without the offset
  // that reserved space for it).
  const withNav = showsEnterpriseNav(sharedChat.origin, platformReader);
  // The owner's own chat: for a marketplace chat that lives under the study, not on
  // the platform chat route their account can't open.
  const ownChatPath =
    continuePath(sharedChat.origin, sharedChat.marketplace_study_id, platformReader, true) ??
    `/ask-natural-organic/${sharedChat.session_id}`;

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      {withNav && <Sidebar />}
      <div className={`flex-1 flex flex-col overflow-hidden ${withNav ? 'md:ml-16' : ''}`}>
        {/* Header - same as AskNaturalOrganicChat */}
        <header
          className={`bg-white border-b border-[#F1F5F9] px-6 py-3 md:mt-0 ${withNav ? 'mt-16' : ''}`}
        >
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <Leaf className="w-4.5 h-4.5" />
              </div>
              <div className="min-w-0">
                <h1 className="font-semibold text-[#1C1C1E] text-sm">
                  {sharedChat.title || 'Shared Chat'}
                </h1>
                {/* The audience REPLACES the old message count: a recipient can see how
                    many messages there are by scrolling, but had no way at all to tell
                    which cohort answered. Wraps on a phone rather than truncating —
                    truncation cut the cohort name, which is the whole point of the line. */}
                {audienceLine && (
                  <p className="text-xs text-[#9CA3AF] md:truncate" title={audienceLine}>
                    {audienceLine}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Owner: Back to Chat button */}
              {sharedChat.is_owner ? (
                <button
                  onClick={() => navigate(ownChatPath)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Chat
                </button>
              ) : isAuthenticated && !sharedChat.has_segment_access ? (
                /* No access indicator */
                <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#6B7280]">
                  <Lock className="w-4 h-4" />
                  No Access
                </span>
              ) : (
                sharedChat.can_fork &&
                (!isAuthenticated || sharedChat.has_segment_access) && (
                  /* Continue button for non-owners with access */
                  <button
                    onClick={handleFork}
                    disabled={isForking}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors disabled:opacity-50"
                  >
                    {isForking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isAuthenticated ? (
                      <MessageSquare className="w-4 h-4" />
                    ) : (
                      <LogIn className="w-4 h-4" />
                    )}
                    {isForking
                      ? 'Continuing...'
                      : isAuthenticated
                        ? continueLabel(sharedChat.origin, sharedChat.is_owner, platformReader)
                        : signInLabel(sharedChat.origin)}
                  </button>
                )
              )}

              <a
                href={homePath(sharedChat.origin, platformReader)}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-16" />
              </a>
            </div>
          </div>
        </header>

        {/* Body: chat only (no sidebar for shared view) */}
        <div className="flex-1 flex overflow-hidden">
          {/* Center — Chat */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
              <div className="max-w-[960px] mx-auto px-6 py-6">
                {/* Messages - same styling as AskNaturalOrganicChat */}
                <div className="space-y-5">
                  {sharedChat.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`rounded-2xl px-5 py-4 ${
                          msg.role === 'user'
                            ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                            : `bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm ${
                                hasRenderableVisual(msg.metadata?.visual) ? 'w-full' : 'max-w-full'
                              }`
                        }`}
                      >
                        {msg.role === 'assistant' ? (
                          <div className="max-w-none">
                            {hasRenderableVisual(msg.metadata?.visual) ? (
                              <InsightToggleView
                                content={msg.content}
                                visual={msg.metadata?.visual}
                              />
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                              >
                                {msg.content}
                              </ReactMarkdown>
                            )}
                            <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                              <button
                                onClick={() => handleCopyMessage(msg.content, idx)}
                                className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                                  copiedMessageIndex === idx
                                    ? 'text-[#10B981] bg-[#ECFDF5]'
                                    : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                                }`}
                                title="Copy to clipboard"
                              >
                                {copiedMessageIndex === idx ? (
                                  <>
                                    <Check className="w-3.5 h-3.5" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3.5 h-3.5" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom CTA */}
                {sharedChat.is_owner ? (
                  <div className="mt-8 p-6 bg-[#E8FCFA] rounded-xl border border-[#07E5D1]/20 text-center">
                    <h3 className="font-semibold text-[#1C1C1E] mb-2">This is your chat</h3>
                    <p className="text-sm text-[#6B7280] mb-4">
                      Go back to your chat to continue the conversation.
                    </p>
                    <button
                      onClick={() => navigate(ownChatPath)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Chat
                    </button>
                  </div>
                ) : isAuthenticated && !sharedChat.has_segment_access ? (
                  <div className="mt-8 p-6 bg-[#FEF2F2] rounded-xl border border-[#FECACA] text-center">
                    <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-6 h-6 text-[#EF4444]" />
                    </div>
                    <h3 className="font-semibold text-[#1C1C1E] mb-2">Access Restricted</h3>
                    <p className="text-sm text-[#6B7280]">
                      You don't have access to fork this chat. Contact your administrator for
                      access.
                    </p>
                  </div>
                ) : (
                  sharedChat.can_fork &&
                  (!isAuthenticated || sharedChat.has_segment_access) && (
                    <div className="mt-8 p-6 bg-[#E8FCFA] rounded-xl border border-[#07E5D1]/20 text-center">
                      <h3 className="font-semibold text-[#1C1C1E] mb-2">
                        {continueHeading(sharedChat.origin, sharedChat.is_owner, platformReader)}
                      </h3>
                      {/* Names WHICH product to sign in to: the two have separate
                        accounts, so "your own account" is ambiguous on a
                        marketplace share and led readers to the wrong login. */}
                      <p className="text-sm text-[#6B7280] mb-4">
                        {continuePrompt(
                          sharedChat.origin,
                          isAuthenticated,
                          sharedChat.is_owner,
                          platformReader,
                        )}
                      </p>
                      <button
                        onClick={handleFork}
                        disabled={isForking}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium disabled:opacity-50"
                      >
                        {isForking ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isAuthenticated ? (
                          <MessageSquare className="w-4 h-4" />
                        ) : (
                          <LogIn className="w-4 h-4" />
                        )}
                        {isForking
                          ? 'Continuing...'
                          : isAuthenticated
                            ? continueLabel(sharedChat.origin, sharedChat.is_owner, platformReader)
                            : signInLabel(sharedChat.origin)}
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SharedChatView;
